import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from '@tanstack/react-router';
import { Separator } from '@/components/ui/separator';
import { SmartLinkExplainerAnimation } from '@/routes/_layout/smart-link/-components/smart-link-animation';
export const SmartLinkExplainer = () => (<main className="mx-auto max-w-5xl px-6 py-12 space-y-16">
        <Hero />
        <Definition />

        <section>
            <h2 className="text-2xl font-semibold">See it in action</h2>
            <div className="mt-6">
                <SmartLinkExplainerAnimation />
            </div>
        </section>

        <HowItWorks />
        <WhatYouTrack />
        <NextClickMatters />
        <WhereItWorks />
        <FAQ />
        <CTA />
    </main>);
function Hero() {
    return (<section className="grid gap-10 md:grid-cols-2">
            <div className="space-y-5">
                <Badge variant="secondary">Explainer</Badge>

                <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">What is an Adora Smart Link?</h1>

                <p className="text-muted-foreground text-lg">
                    Track <strong>intent</strong>, not just clicks. See when people scan, where they are, and what they
                    do next.
                </p>

                <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                    {['QR scans', 'Link clicks', 'Location signal', 'Next action'].map((x) => (<span key={x} className="rounded-full border px-3 py-1">
                            {x}
                        </span>))}
                </div>

                <div className="flex gap-3">
                    <Button size="lg" asChild>
                        <Link to="/sign-up">Create Smart Link</Link>
                    </Button>
                    <Button size="lg" variant="outline" asChild>
                        <a href="#how">See how it works</a>
                    </Button>
                </div>
            </div>

            <SmartLinkPreviewCard />
        </section>);
}
function Definition() {
    return (<Card>
            <CardHeader>
                <CardTitle>Simple definition</CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground">
                A <strong className="text-foreground">Smart Link</strong> is a QR code or short link that records
                <strong className="text-foreground"> when</strong>,<strong className="text-foreground"> where</strong>,
                and
                <strong className="text-foreground"> what people do next</strong>.
            </CardContent>
        </Card>);
}
function HowItWorks() {
    const steps = [
        ['Share one Smart Link', 'Use it as a QR code or short URL'],
        ['People scan or click', 'Adora records time and context'],
        ['They land on actions', 'Menu, contact, directions, order'],
        ['Adora tracks next action', 'The strongest intent signal'],
    ];
    return (<section id="how">
            <h2 className="text-2xl font-semibold">How it works</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
                {steps.map(([title, desc], i) => (<Card key={i}>
                        <CardContent className="p-6">
                            <p className="font-semibold">
                                {i + 1}. {title}
                            </p>
                            <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
                        </CardContent>
                    </Card>))}
            </div>
        </section>);
}
function WhatYouTrack() {
    const items = [
        ['When', 'Hourly and daily patterns'],
        ['Where', 'City-level location signals'],
        ['Device', 'Basic device & channel context'],
        ['Next action', 'What people actually wanted'],
    ];
    return (<Card>
            <CardHeader>
                <CardTitle>What you can track</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
                {items.map(([t, d]) => (<div key={t} className="rounded-xl bg-muted/40 p-4">
                        <p className="font-medium">{t}</p>
                        <p className="text-sm text-muted-foreground">{d}</p>
                    </div>))}
            </CardContent>
        </Card>);
}
function NextClickMatters() {
    return (<section>
            <h2 className="text-2xl font-semibold">Why “what they click next” matters</h2>
            <p className="mt-2 text-muted-foreground max-w-2xl">
                Clicks only tell you someone arrived. The next action tells you what they were trying to do.
            </p>
        </section>);
}
function WhereItWorks() {
    const places = ['Flyers', 'Packaging', 'Posters', 'Menus', 'Banners'];
    return (<Card>
            <CardHeader>
                <CardTitle>Where Smart Links work best</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-3">
                {places.map((p) => (<div key={p} className="rounded-xl bg-muted/40 p-4">
                        <p className="font-medium">{p}</p>
                    </div>))}
            </CardContent>
        </Card>);
}
function CTA() {
    return (<Card>
            <CardContent className="p-8 flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                    <h3 className="text-xl font-semibold">Stop guessing. Start tracking.</h3>
                    <p className="text-sm text-muted-foreground">
                        Create a Smart Link and understand real-world intent.
                    </p>
                </div>

                <div className="flex gap-2">
                    <Button size="lg" asChild>
                        <Link to="/sign-up">Get started</Link>
                    </Button>
                    <Button size="lg" variant="outline" asChild>
                        <Link to="/sign-in">Sign in</Link>
                    </Button>
                </div>
            </CardContent>
        </Card>);
}
function FAQ() {
    const faqs = [
        {
            q: 'Is a Smart Link just a short link?',
            a: 'No. It’s a short link + a tracking layer that records next actions.',
        },
        {
            q: 'Do I need a website?',
            a: 'No. You can use a simple Smart Link landing page with actions like Menu, Contact, Directions.',
        },
        { q: 'What does “intent” mean here?', a: 'Intent is what people do after they land—what they tap next.' },
        {
            q: 'Can I use it online too?',
            a: 'Yes. Works for social posts too, but especially powerful for QR codes in the real world.',
        },
    ];
    return (<section id="faq" className="mt-12">
            <h2 className="text-xl font-semibold tracking-tight md:text-2xl">FAQ</h2>
            <div className="mt-6 grid gap-4">
                {faqs.map((f) => (<Card key={f.q} className="rounded-2xl">
                        <CardContent className="p-6">
                            <p className="font-semibold">{f.q}</p>
                            <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                        </CardContent>
                    </Card>))}
            </div>
        </section>);
}
export const SmartLinkPreviewCard = () => {
    return (<Card className="rounded-2xl border shadow-sm">
            <CardContent className="p-6 space-y-5">
                {/* Header */}
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="text-xs text-muted-foreground">Example Smart Link</p>
                        <p className="mt-1 font-semibold text-sm">adora.media/c/coffee-menu</p>
                    </div>
                    <Badge variant="secondary">Preview</Badge>
                </div>

                <Separator />

                {/* Key stats */}
                <div className="grid grid-cols-2 gap-3">
                    <MiniStat label="Scans / clicks" value="1,248" hint="Last 7 days"/>
                    <MiniStat label="Top city" value="Phnom Penh" hint="Most activity"/>
                    <MiniStat label="Top intent" value="Menu" hint="Next click"/>
                    <MiniStat label="Peak time" value="12:05" hint="Lunch signal"/>
                </div>

                <Separator />

                {/* Intent distribution */}
                <div className="space-y-3">
                    <p className="text-sm font-medium">What people click next</p>

                    <IntentRow label="Menu" percent={52}/>
                    <IntentRow label="Call / Contact" percent={27}/>
                    <IntentRow label="Directions" percent={21}/>
                </div>

                {/* Footer note */}
                <p className="pt-2 text-xs text-muted-foreground">
                    This is what makes a Smart Link different: not just clicks, but
                    <span className="font-medium text-foreground"> intent after the click</span>.
                </p>
            </CardContent>
        </Card>);
};
/* ---------- Sub components ---------- */
function MiniStat(props) {
    return (<div className="rounded-xl border bg-muted/30 p-3">
            <p className="text-[11px] text-muted-foreground">{props.label}</p>
            <p className="mt-1 text-base font-semibold">{props.value}</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{props.hint}</p>
        </div>);
}
function IntentRow(props) {
    return (<div className="space-y-1">
            <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{props.label}</span>
                <span className="text-muted-foreground">{props.percent}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted">
                <div className="h-2 rounded-full bg-primary" style={{ width: `${props.percent}%` }}/>
            </div>
        </div>);
}
